/**
 * Analytics Module
 * Handles Google Analytics 4 event tracking for CTAs and user interactions
 */

import { $$, ifExists } from '../utils/dom.js';

export class AnalyticsManager {
  constructor() {
    this.gtagReady = false;
    this.checkGtagAvailability();
  }

  /**
   * Check if gtag is available, wait if necessary
   */
  checkGtagAvailability() {
    if (typeof window.gtag === 'function') {
      this.gtagReady = true;
      return;
    }

    // Wait for gtag to load (max 5 seconds)
    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(() => {
      attempts++;
      if (typeof window.gtag === 'function') {
        this.gtagReady = true;
        clearInterval(checkInterval);
        console.log('✅ Google Analytics ready');
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.warn('⚠️ Google Analytics (gtag) not found after 5s. Event tracking disabled.');
      }
    }, 100);
  }

  /**
   * Initialize all analytics tracking
   */
  init() {
    // Wait for gtag to be ready before setting up tracking
    const initTracking = () => {
      if (!this.gtagReady) {
        setTimeout(initTracking, 100);
        return;
      }

      this.trackArticleClicks();
      this.trackCTAButtons();
      this.trackNavigationLinks();
      console.log('📊 Analytics tracking initialized');
    };

    initTracking();
  }

  /**
   * Send event to Google Analytics
   * @param {string} eventName - Name of the event
   * @param {Object} eventParams - Additional event parameters
   */
  sendEvent(eventName, eventParams = {}) {
    if (!this.gtagReady) return;

    try {
      window.gtag('event', eventName, {
        ...eventParams,
        timestamp: new Date().toISOString()
      });
      console.log(`📊 Analytics event sent: ${eventName}`, eventParams);
    } catch (error) {
      console.error('Failed to send analytics event:', error);
    }
  }

  /**
   * Track article/publication clicks
   */
  trackArticleClicks() {
    const articleLinks = $$('.article-link');

    articleLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const article = e.currentTarget;
        const title = article.querySelector('.article-publication')?.textContent?.trim() || 'Unknown';
        const publication = article.querySelector('.article h2')?.textContent?.trim() || 'Unknown';
        const author = article.querySelector('.article-author')?.textContent?.trim() || 'Unknown';
        const date = article.querySelector('.article-date')?.textContent?.trim() || 'Unknown';
        const url = article.href;

        this.sendEvent('article_click', {
          event_category: 'Publications',
          event_label: title,
          article_title: title,
          publication_name: publication,
          author: author,
          publish_date: date,
          destination_url: url
        });
      });
    });

    console.log(`Tracking ${articleLinks.length} article links`);
  }

  /**
   * Track CTA button clicks
   */
  trackCTAButtons() {
    const ctaButtons = $$('.about-cta, .cta-button, .contact-cta');

    ctaButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const buttonText = e.currentTarget.textContent?.trim() || 'Unknown';
        const buttonHref = e.currentTarget.href || window.location.href;
        const pageLocation = window.location.pathname;

        this.sendEvent('cta_click', {
          event_category: 'CTA',
          event_label: buttonText,
          button_text: buttonText,
          button_destination: buttonHref,
          page_location: pageLocation
        });
      });
    });

    console.log(`Tracking ${ctaButtons.length} CTA buttons`);
  }

  /**
   * Track navigation link clicks
   */
  trackNavigationLinks() {
    const navLinks = $$('nav a, .slide-menu a');

    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const linkText = e.currentTarget.textContent?.trim() || 'Unknown';
        const destination = e.currentTarget.href || '#';

        this.sendEvent('navigation_click', {
          event_category: 'Navigation',
          event_label: linkText,
          link_text: linkText,
          destination_url: destination
        });
      });
    });

    console.log(`Tracking ${navLinks.length} navigation links`);
  }

  /**
   * Track custom events (for ad-hoc tracking needs)
   * @param {string} eventName - Custom event name
   * @param {Object} params - Event parameters
   */
  trackCustomEvent(eventName, params = {}) {
    this.sendEvent(eventName, {
      event_category: 'Custom',
      ...params
    });
  }
}
